import type { SerializedError } from '@reduxjs/toolkit'
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query'
import { Checkbox } from '@thanx/uikit/checkbox'
import { Notification } from '@thanx/uikit/notification'
import { Text } from '@thanx/uikit/text'
import type { RestrictCustomerParams } from 'api/recoveryAi.types'
import { isFetchBaseQueryError } from 'api/utils'
import ChatBubbleSlash from 'components/Icons/ChatBubbleSlash'
import CheckCircleOutline from 'components/Icons/CheckCircleOutline'
import GiftOutline from 'components/Icons/GiftOutline'
import ModalContainer from 'components/ModalContainer'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React, { useState } from 'react'
import { useStyletron } from 'styletron-react'
import { useAbility } from 'utilities/ability'

const t = buildTranslate('recovery_ai.case_detail.restrict')

// "What this means" rows, in design order.
const MEANS_ROWS = [
  { key: 'incentives', Icon: GiftOutline },
  { key: 'feedback', Icon: ChatBubbleSlash },
  { key: 'resolved', Icon: CheckCircleOutline },
] as const

type Props = {
  isOpen: boolean
  conversationId: string
  // The customer's other OPEN escalations (excluding this case). > 0 shows the
  // "close them out too?" panel; 0 (the default) makes this a plain restrict
  // confirm. Optional so the gaming-review queue — a different surface where the
  // case-file escalations panel doesn't apply — can mount this unchanged.
  otherOpenEscalationsCount?: number
  // The ids behind otherOpenEscalationsCount. Echoed back verbatim as
  // close_escalation_ids when the operator confirms with the checkbox checked —
  // the backend only ever closes ids it's given, never re-derives "every open
  // escalation right now" (nexus review LessThanInfinity). Optional/defaulted
  // alongside otherOpenEscalationsCount for the same gaming-review reason.
  otherOpenEscalationIds?: string[]
  // PII-gated — null without consent; the panel heading falls back to a generic
  // subject ("This customer") when absent.
  customerName?: string | null
  // The caller's mutation (components/ stays presentational). Resolves on
  // success, and the modal closes itself; rejects on failure, leaving
  // isError/error to render the banner. Mirrors components/GrantRewardModal.
  onConfirm: (params: RestrictCustomerParams) => Promise<unknown>
  isSubmitting: boolean
  isError: boolean
  // A 422 means "already restricted" (a retry would 422 again).
  error?: FetchBaseQueryError | SerializedError
  onResetError: () => void
  onClose: () => void
}

// Operator action: restrict the customer behind this case. A single POST both
// blocks the customer from RecoveryAI AND auto-resolves the case server-side
// (#569) — so there is no separate resolve call here. Presentational: the
// caller owns useRestrictCustomerMutation (src/components rule) and passes its
// state through onConfirm/isSubmitting/isError/error/onResetError. Reuses the
// shared ModalContainer (Base Web Modal — owns focus-trap/ESC/aria).
const RestrictCustomerModal = ({
  isOpen,
  conversationId,
  otherOpenEscalationsCount = 0,
  otherOpenEscalationIds = [],
  customerName = null,
  onConfirm,
  isSubmitting,
  isError,
  error,
  onResetError,
  onClose,
}: Props) => {
  const [css] = useStyletron()
  const ability = useAbility()
  // Checked by default (design): the common case is to clear the customer's
  // other open cases alongside the restrict.
  const [closeOthers, setCloseOthers] = useState(true)
  // A 422 here specifically means "this customer is already restricted" (the
  // dominant failure — see api/recoveryAi.ts's restrictCustomer comment); the
  // generic "please try again" copy is actively wrong for it since a retry
  // always 422s again.
  const isAlreadyRestrictedError =
    isFetchBaseQueryError(error) && error.status === 422

  // Defense-in-depth (D6): CaseDetail only mounts this for managers; this
  // re-check keeps the modal UI + confirm action from RENDERING for a view-only
  // operator.
  if (ability.cannot('manage', 'MerchantSettings')) return null

  const hasOtherEscalations = otherOpenEscalationsCount > 0
  // Per-instance id so the checkbox can aria-describedby the helper copy.
  // Namespaced by conversation (React 16 has no useId) to stay unique if two
  // restrict modals ever mount at once.
  const helperId = `restrict-escalations-helper-${conversationId}`

  const handleClose = () => {
    // Ignore close requests (Cancel / backdrop / ESC) while a restrict is in
    // flight: the in-flight POST can't be aborted client-side and would still
    // resolve and re-invoke this via handleConfirm's .then, double-firing
    // onResetError()/onClose() — and a mid-flight reset has murky RTK semantics.
    // Confirm is already gated via canProceed; this locks the close path until
    // the mutation settles.
    if (isSubmitting) return
    // Clear any failed-restrict error so a stale banner doesn't show on reopen,
    // and re-check the box for the next open — this modal instance is mounted
    // persistently (only isOpen toggles).
    onResetError()
    setCloseOthers(true)
    onClose()
  }

  const handleConfirm = () => {
    onConfirm({
      conversation_id: conversationId,
      // Echo back the exact ids the operator was shown (not a boolean "close
      // everything open") only when they opted in; otherwise omit so the
      // request stays the plain single-case restrict.
      close_escalation_ids:
        hasOtherEscalations && closeOthers ? otherOpenEscalationIds : undefined,
    })
      .then(handleClose)
      // The failure is surfaced inline via isError below; swallow the rejection
      // so it isn't an unhandled promise.
      .catch(() => {})
  }

  const escalationHeading =
    otherOpenEscalationsCount === 1
      ? t('escalations.heading_one', {
          name: customerName || t('escalations.default_subject'),
        })
      : t('escalations.heading_other', {
          name: customerName || t('escalations.default_subject'),
          count: otherOpenEscalationsCount,
        })

  return (
    <ModalContainer
      isOpen={isOpen}
      onClose={handleClose}
      title={t('heading')}
      ariaLabel={t('aria_label')}
      proceedText={t('confirm')}
      onProceed={handleConfirm}
      // Disable the confirm while a restrict is in flight so a double-click
      // can't double-POST.
      canProceed={!isSubmitting}
      isLoading={isSubmitting}
    >
      <Text.BodyText4 tag="p" color="grey70" className="mt-s mb-0">
        {t('subtitle')}
      </Text.BodyText4>
      <Text.BodyText5
        tag="p"
        bold
        color="grey50"
        className={css({
          margin: '24px 0 12px',
          textTransform: 'uppercase',
          letterSpacing: '0.7px',
        })}
      >
        {t('means_label')}
      </Text.BodyText5>
      <ul
        className={css({
          listStyle: 'none',
          margin: 0,
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
        })}
      >
        {MEANS_ROWS.map(({ key, Icon }) => (
          <li
            key={key}
            className={css({
              display: 'flex',
              gap: '12px',
              alignItems: 'flex-start',
            })}
          >
            <Icon
              className={css({ flexShrink: 0 })}
              strokeColor={palette.grey90}
            />
            <Text.BodyText4 tag="span" color="grey90">
              {t(`means.${key}`)}
            </Text.BodyText4>
          </li>
        ))}
      </ul>

      {hasOtherEscalations && (
        <div
          className={css({
            marginTop: '24px',
            padding: '16px',
            border: `1px solid ${palette.grey20}`,
            borderRadius: '8px',
            backgroundColor: palette.grey05,
          })}
        >
          <Text.BodyText4 bold tag="p" color="grey90" className="mb-xs">
            {escalationHeading}
          </Text.BodyText4>
          <Checkbox
            checked={closeOthers}
            onChange={setCloseOthers}
            aria-describedby={helperId}
          >
            {t('escalations.checkbox')}
          </Checkbox>
          {/* uikit Text drops unknown props, so the id for aria-describedby
              lives on a wrapper (its text content is the description). */}
          <div id={helperId}>
            <Text.BodyText5 tag="p" color="grey70" className="mt-xxs mb-0">
              {t('escalations.helper')}
            </Text.BodyText5>
          </div>
        </div>
      )}

      {isError && (
        <div className={css({ marginTop: '12px' })}>
          <Notification
            kind="negative"
            size="full"
            closeable
            onClose={onResetError}
          >
            {isAlreadyRestrictedError
              ? t('already_restricted_error')
              : t('error')}
          </Notification>
        </div>
      )}
    </ModalContainer>
  )
}

export default RestrictCustomerModal
